import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Avatar,
  Paper,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  InputAdornment,
  Divider,
  useMediaQuery,
  Autocomplete,
  Badge,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AddIcon from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DesignChatThread from '../components/DesignChatThread';
import MessagingThread from '../components/MessagingThread';
import DesignPreviewDialog from '../components/DesignPreviewDialog';
import { designApi } from '../api/design';
import { messagingApi, ChatConversation, MessagingUser } from '../api/messaging';
import api from '../services/api';

interface DesignConversation {
  id: number;
  design_no: string;
  image_url: string | null;
  last_message: string;
  last_by: string;
  last_at: string;
  message_count: number;
  unread_count: number;
}

interface DesignOption {
  id: number;
  design_no: string;
  image_url?: string | null;
}

function getCurrentUser(): { id?: number; role?: string; name?: string } {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
}

export default function DesignChatPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const currentUser = getCurrentUser();
  const isAdminOrMaster = currentUser.role === 'MASTER' || currentUser.role === 'ADMIN';

  const designIdParam = searchParams.get('designId');
  const designNoParam = searchParams.get('designNo');

  // Tab state
  const [activeTab, setActiveTab] = useState<'designs' | 'direct' | 'groups'>('designs');

  // Designs tab state
  const [conversations, setConversations] = useState<DesignConversation[]>([]);
  const [allDesigns, setAllDesigns] = useState<DesignOption[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDesign, setSelectedDesign] = useState<{ id: number; design_no: string; image_url?: string | null } | null>(null);
  const [showNewDesignChat, setShowNewDesignChat] = useState(false);

  // Messaging tab state
  const [msgConversations, setMsgConversations] = useState<ChatConversation[]>([]);
  const [loadingMsgConvs, setLoadingMsgConvs] = useState(false);
  const [msgSearchTerm, setMsgSearchTerm] = useState('');
  const [selectedConvId, setSelectedConvId] = useState<number | null>(null);
  const [selectedConvName, setSelectedConvName] = useState('');
  const [allUsers, setAllUsers] = useState<MessagingUser[]>([]);

  // New DM dialog
  const [dmDialogOpen, setDmDialogOpen] = useState(false);
  const [dmSearchUser, setDmSearchUser] = useState<MessagingUser | null>(null);
  const [creatingDm, setCreatingDm] = useState(false);

  // New Group dialog
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState<MessagingUser[]>([]);
  const [creatingGroup, setCreatingGroup] = useState(false);

  // Design Preview Dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDesign, setPreviewDesign] = useState<any>(null);
  const [previewTopDesign, setPreviewTopDesign] = useState<any>(null);
  const [previewBottomDesign, setPreviewBottomDesign] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // ───── Design Preview ─────
  const handleOpenPreview = async (designNo: string, designId?: number) => {
    if (loadingPreview) return; // guard against rapid re-clicks
    setLoadingPreview(true);
    setPreviewOpen(true);
    setPreviewDesign(null);
    setPreviewTopDesign(null);
    setPreviewBottomDesign(null);
    try {
      // Use designId directly if available (avoids fetching all designs)
      if (designId && designId > 0) {
        try {
          const fullDesign = await designApi.getById(designId);
          setPreviewDesign(fullDesign?.data || fullDesign || null);
        } catch {
          // continue without design data — dialog will still show cutting info
        }
      }
      const cuttingResponse = await api.get('/design-cutting');
      if (cuttingResponse.data && Array.isArray(cuttingResponse.data)) {
        setPreviewTopDesign(cuttingResponse.data.find((d: any) => d.design_no === designNo && d.garment_part === 'TOP') || null);
        setPreviewBottomDesign(cuttingResponse.data.find((d: any) => d.design_no === designNo && d.garment_part === 'BOTTOM') || null);
      }
    } catch (err) {
      console.error('Error fetching design preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  // ───── Designs tab ─────
  const fetchConversations = useCallback(async () => {
    try {
      setLoadingConversations(true);
      const response = await designApi.getChatConversations();
      setConversations(response.data || []);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  const fetchAllDesigns = useCallback(async () => {
    try {
      setLoadingDesigns(true);
      const response = await api.get('/designs');
      const data = Array.isArray(response.data?.data) ? response.data.data : Array.isArray(response.data) ? response.data : [];
      setAllDesigns(data.map((d: any) => ({
        id: d.id,
        design_no: d.design_no,
        image_url: d.image_url || d.image2_url || d.image3_url || d.image4_url || null
      })));
    } catch (err) {
      console.error('Error fetching designs:', err);
    } finally {
      setLoadingDesigns(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Auto-select design from URL params
  useEffect(() => {
    if (designIdParam || designNoParam) {
      const dNo = designNoParam || '';
      setSelectedDesign({ id: designIdParam ? parseInt(designIdParam) : 0, design_no: dNo, image_url: null });
      const match = conversations.find(c =>
        (designIdParam && c.id === parseInt(designIdParam)) ||
        (designNoParam && c.design_no === designNoParam)
      );
      if (match) {
        setSelectedDesign({ id: match.id, design_no: match.design_no, image_url: match.image_url });
        if (match.unread_count > 0) markDesignAsRead(match.design_no);
      } else if (dNo) {
        markDesignAsRead(dNo);
      }
    }
  }, [designIdParam, designNoParam, conversations]);

  const markDesignAsRead = async (designNo: string) => {
    try {
      await designApi.markChatRead(designNo);
      setConversations(prev => prev.map(c =>
        c.design_no === designNo ? { ...c, unread_count: 0 } : c
      ));
    } catch (err) {
      console.error('Error marking chat as read:', err);
    }
  };

  // ───── Messaging tab ─────
  const fetchMsgConversations = useCallback(async () => {
    try {
      setLoadingMsgConvs(true);
      const data = await messagingApi.getConversations();
      setMsgConversations(data || []);
    } catch (err) {
      console.error('Error fetching messaging conversations:', err);
    } finally {
      setLoadingMsgConvs(false);
    }
  }, []);

  const fetchAllUsers = useCallback(async () => {
    try {
      const data = await messagingApi.getUsers();
      setAllUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'direct' || activeTab === 'groups') {
      fetchMsgConversations();
      if (allUsers.length === 0) fetchAllUsers();
    }
  }, [activeTab, fetchMsgConversations, fetchAllUsers]);

  const filteredMsgConvs = msgConversations.filter(c => {
    const typeMatch = activeTab === 'direct' ? c.type === 'DIRECT' : c.type === 'GROUP';
    const nameMatch = !msgSearchTerm || c.name.toLowerCase().includes(msgSearchTerm.toLowerCase());
    return typeMatch && nameMatch;
  });

  const handleSelectMsgConversation = (conv: ChatConversation) => {
    setSelectedConvId(conv.id);
    setSelectedConvName(conv.name);
    // Update local unread
    setMsgConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
  };

  // New DM
  const handleCreateDm = async () => {
    if (!dmSearchUser) return;
    setCreatingDm(true);
    try {
      const result = await messagingApi.createConversation({ type: 'DIRECT', user_id: dmSearchUser.id });
      setDmDialogOpen(false);
      setDmSearchUser(null);
      await fetchMsgConversations();
      setSelectedConvId(result.id);
      setSelectedConvName(dmSearchUser.name);
    } catch (err) {
      console.error('Error creating DM:', err);
    } finally {
      setCreatingDm(false);
    }
  };

  // New Group
  const handleCreateGroup = async () => {
    if (!groupName.trim() || groupMembers.length === 0) return;
    setCreatingGroup(true);
    try {
      const result = await messagingApi.createConversation({
        type: 'GROUP',
        name: groupName.trim(),
        member_ids: groupMembers.map(m => m.id)
      });
      setGroupDialogOpen(false);
      setGroupName('');
      setGroupMembers([]);
      await fetchMsgConversations();
      setSelectedConvId(result.id);
      setSelectedConvName(groupName.trim());
    } catch (err) {
      console.error('Error creating group:', err);
    } finally {
      setCreatingGroup(false);
    }
  };

  // ───── Navigation ─────
  const handleBack = () => {
    if (isMobile && (selectedDesign || selectedConvId)) {
      setSelectedDesign(null);
      setSelectedConvId(null);
      if (activeTab === 'designs') fetchConversations();
      else fetchMsgConversations();
    } else {
      navigate(-1);
    }
  };

  const formatTime = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHr = Math.floor(diffMs / 3600000);
    if (diffHr < 24) {
      return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    }
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const filteredDesignConvs = searchTerm
    ? conversations.filter(c => c.design_no.toLowerCase().includes(searchTerm.toLowerCase()))
    : conversations;

  const isRightPaneActive = selectedDesign !== null || selectedConvId !== null;
  const showList = isMobile ? !isRightPaneActive : true;
  const showRight = isMobile ? isRightPaneActive : true;

  // ───── Render ─────
  return (
    <Box sx={{
      display: 'flex',
      overflow: 'hidden',
      position: 'fixed',
      top: { xs: 56, sm: 64 },
      left: { xs: 0, sm: '240px' },
      right: 0,
      bottom: 0,
      zIndex: 1,
      bgcolor: 'background.paper'
    }}>

      {/* ===== LEFT: Conversation List ===== */}
      {showList && (
        <Box sx={{
          width: isMobile ? '100%' : 340,
          flexShrink: 0,
          borderRight: isMobile ? 0 : 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper'
        }}>
          {/* Header */}
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
            {isMobile && (
              <IconButton size="small" onClick={() => navigate(-1)}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>Chats</Typography>
            {/* Action button per tab */}
            {activeTab === 'designs' && (
              <IconButton
                size="small"
                color="primary"
                onClick={() => { setShowNewDesignChat(!showNewDesignChat); if (!showNewDesignChat && allDesigns.length === 0) fetchAllDesigns(); }}
                title="New Design Chat"
              >
                <ChatBubbleOutlineIcon fontSize="small" />
              </IconButton>
            )}
            {activeTab === 'direct' && (
              <IconButton size="small" color="primary" title="New Direct Message" onClick={() => { setDmDialogOpen(true); if (allUsers.length === 0) fetchAllUsers(); }}>
                <PersonIcon fontSize="small" />
              </IconButton>
            )}
            {activeTab === 'groups' && isAdminOrMaster && (
              <IconButton size="small" color="primary" title="New Group" onClick={() => { setGroupDialogOpen(true); if (allUsers.length === 0) fetchAllUsers(); }}>
                <AddIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => {
              setActiveTab(v);
              setSelectedDesign(null);
              setSelectedConvId(null);
              setMsgSearchTerm('');
              setSearchTerm('');
            }}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
          >
            <Tab value="designs" label="Designs" sx={{ minHeight: 40, fontSize: '0.75rem', py: 0.5 }} />
            <Tab
              value="direct"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Direct
                  {msgConversations.filter(c => c.type === 'DIRECT' && c.unread_count > 0).length > 0 && (
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                  )}
                </Box>
              }
              sx={{ minHeight: 40, fontSize: '0.75rem', py: 0.5 }}
            />
            <Tab
              value="groups"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Groups
                  {msgConversations.filter(c => c.type === 'GROUP' && c.unread_count > 0).length > 0 && (
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                  )}
                </Box>
              }
              sx={{ minHeight: 40, fontSize: '0.75rem', py: 0.5 }}
            />
          </Tabs>

          {/* ── Designs Tab ── */}
          {activeTab === 'designs' && (
            <>
              {showNewDesignChat && (
                <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
                  <Autocomplete
                    size="small"
                    options={allDesigns}
                    loading={loadingDesigns}
                    getOptionLabel={(o) => o.design_no}
                    onChange={(_, v) => {
                      if (v) { setSelectedDesign({ id: v.id, design_no: v.design_no, image_url: v.image_url }); setShowNewDesignChat(false); }
                    }}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar src={option.image_url || ''} variant="rounded" sx={{ width: 28, height: 28, fontSize: '0.65rem' }}>
                          {option.design_no[0]}
                        </Avatar>
                        <Typography variant="body2">{option.design_no}</Typography>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Search design to start chat..." autoFocus
                        InputProps={{ ...params.InputProps, endAdornment: (<>{loadingDesigns ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) }}
                      />
                    )}
                    openOnFocus
                  />
                </Box>
              )}
              <Box sx={{ px: 2, py: 1 }}>
                <TextField
                  size="small" fullWidth placeholder="Search conversations..."
                  value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
                    sx: { borderRadius: 2, fontSize: '0.85rem' }
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {loadingConversations ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                ) : filteredDesignConvs.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'No conversations found' : 'No conversations yet'}
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {filteredDesignConvs.map((conv) => (
                      <React.Fragment key={conv.design_no}>
                        <ListItemButton
                          selected={selectedDesign?.design_no === conv.design_no}
                          onClick={() => {
                            setSelectedDesign({ id: conv.id, design_no: conv.design_no, image_url: conv.image_url });
                            setShowNewDesignChat(false);
                            if (conv.unread_count > 0) markDesignAsRead(conv.design_no);
                          }}
                          sx={{
                            py: 1.5, px: 2,
                            bgcolor: conv.unread_count > 0 ? 'action.hover' : 'transparent',
                            borderLeft: conv.unread_count > 0 ? '3px solid' : '3px solid transparent',
                            borderLeftColor: conv.unread_count > 0 ? 'primary.main' : 'transparent'
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 48 }}>
                            <Badge badgeContent={conv.unread_count} color="primary" max={99} invisible={conv.unread_count === 0} overlap="rectangular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                              <Avatar src={conv.image_url || ''} variant="rounded" sx={{ width: 40, height: 40 }}>{conv.design_no[0]}</Avatar>
                            </Badge>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" fontWeight={conv.unread_count > 0 ? 700 : 500} noWrap>{conv.design_no}</Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', flexShrink: 0, ml: 1, color: conv.unread_count > 0 ? 'primary.main' : 'text.disabled', fontWeight: conv.unread_count > 0 ? 600 : 400 }}>
                                  {formatTime(conv.last_at)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" noWrap sx={{ display: 'block', color: conv.unread_count > 0 ? 'text.primary' : 'text.secondary', fontWeight: conv.unread_count > 0 ? 500 : 400 }}>
                                {conv.last_by}: {conv.last_message}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                        <Divider component="li" />
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            </>
          )}

          {/* ── Direct / Groups Tab ── */}
          {(activeTab === 'direct' || activeTab === 'groups') && (
            <>
              <Box sx={{ px: 2, py: 1 }}>
                <TextField
                  size="small" fullWidth placeholder={activeTab === 'direct' ? 'Search direct messages...' : 'Search groups...'}
                  value={msgSearchTerm} onChange={(e) => setMsgSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
                    sx: { borderRadius: 2, fontSize: '0.85rem' }
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {loadingMsgConvs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                ) : filteredMsgConvs.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    {activeTab === 'direct'
                      ? <PersonIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      : <GroupIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />}
                    <Typography variant="body2" color="text.secondary">
                      {msgSearchTerm ? 'No results' : activeTab === 'direct' ? 'No direct messages yet' : 'No groups yet'}
                    </Typography>
                    {!msgSearchTerm && activeTab === 'direct' && (
                      <Typography variant="caption" color="text.disabled">Tap the person icon above to start a DM</Typography>
                    )}
                    {!msgSearchTerm && activeTab === 'groups' && isAdminOrMaster && (
                      <Typography variant="caption" color="text.disabled">Tap + above to create a group</Typography>
                    )}
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {filteredMsgConvs.map((conv) => (
                      <React.Fragment key={conv.id}>
                        <ListItemButton
                          selected={selectedConvId === conv.id}
                          onClick={() => handleSelectMsgConversation(conv)}
                          sx={{
                            py: 1.5, px: 2,
                            bgcolor: conv.unread_count > 0 ? 'action.hover' : 'transparent',
                            borderLeft: conv.unread_count > 0 ? '3px solid' : '3px solid transparent',
                            borderLeftColor: conv.unread_count > 0 ? 'primary.main' : 'transparent'
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 48 }}>
                            <Badge badgeContent={conv.unread_count} color="primary" max={99} invisible={conv.unread_count === 0} overlap="rectangular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                              <Avatar sx={{ width: 40, height: 40, bgcolor: activeTab === 'direct' ? 'secondary.main' : 'primary.main', fontSize: '1rem' }}>
                                {conv.type === 'GROUP' ? <GroupIcon sx={{ fontSize: 20 }} /> : conv.avatar_letter}
                              </Avatar>
                            </Badge>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" fontWeight={conv.unread_count > 0 ? 700 : 500} noWrap>{conv.name}</Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', flexShrink: 0, ml: 1, color: conv.unread_count > 0 ? 'primary.main' : 'text.disabled', fontWeight: conv.unread_count > 0 ? 600 : 400 }}>
                                  {formatTime(conv.last_at)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              conv.last_message ? (
                                <Typography variant="caption" noWrap sx={{ display: 'block', color: conv.unread_count > 0 ? 'text.primary' : 'text.secondary', fontWeight: conv.unread_count > 0 ? 500 : 400 }}>
                                  {conv.last_by ? `${conv.last_by}: ` : ''}{conv.last_message}
                                </Typography>
                              ) : (
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>No messages yet</Typography>
                              )
                            }
                          />
                        </ListItemButton>
                        <Divider component="li" />
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* ===== RIGHT: Chat Thread ===== */}
      {showRight && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%', overflow: 'hidden' }}>

          {/* ── Designs right pane ── */}
          {activeTab === 'designs' && (
            selectedDesign ? (
              <>
                <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderRadius: 0, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                  {isMobile && (
                    <IconButton size="small" onClick={handleBack}><ArrowBackIcon /></IconButton>
                  )}
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                    onClick={() => handleOpenPreview(selectedDesign.design_no, selectedDesign.id)}
                  >
                    <Avatar src={selectedDesign.image_url || ''} variant="rounded" sx={{ width: 38, height: 38 }}>{selectedDesign.design_no[0]}</Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap color="primary">{selectedDesign.design_no}</Typography>
                      <Typography variant="caption" color="text.secondary">Tap to view design details</Typography>
                    </Box>
                  </Box>
                </Paper>
                <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
                  <DesignChatThread key={selectedDesign.id} designId={selectedDesign.id} designNo={selectedDesign.design_no} hideHeader />
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, bgcolor: 'grey.50' }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">Design Discussion</Typography>
                <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ mt: 0.5 }}>
                  Select a conversation from the left or start a new one
                </Typography>
              </Box>
            )
          )}

          {/* ── Direct / Groups right pane ── */}
          {(activeTab === 'direct' || activeTab === 'groups') && (
            selectedConvId ? (
              <>
                {isMobile && (
                  <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderRadius: 0, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                    <IconButton size="small" onClick={handleBack}><ArrowBackIcon /></IconButton>
                    <Avatar sx={{ width: 38, height: 38, bgcolor: activeTab === 'groups' ? 'primary.main' : 'secondary.main' }}>
                      {activeTab === 'groups' ? <GroupIcon /> : selectedConvName[0]}
                    </Avatar>
                    <Typography variant="subtitle2" fontWeight={700} noWrap>{selectedConvName}</Typography>
                  </Paper>
                )}
                <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
                  <MessagingThread
                    key={selectedConvId}
                    conversationId={selectedConvId}
                    conversationName={selectedConvName}
                    conversationType={activeTab === 'groups' ? 'GROUP' : 'DIRECT'}
                    onMessageSent={fetchMsgConversations}
                  />
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, bgcolor: 'grey.50' }}>
                {activeTab === 'direct'
                  ? <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  : <GroupIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />}
                <Typography variant="h6" color="text.secondary">
                  {activeTab === 'direct' ? 'Direct Messages' : 'Group Chats'}
                </Typography>
                <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ mt: 0.5 }}>
                  {activeTab === 'direct' ? 'Select a conversation or start a new DM' : 'Select a group or create a new one'}
                </Typography>
              </Box>
            )
          )}
        </Box>
      )}

      {/* Design Preview Dialog */}
      <DesignPreviewDialog
        open={previewOpen}
        design={previewDesign}
        designNo={selectedDesign?.design_no}
        topDesign={previewTopDesign}
        bottomDesign={previewBottomDesign}
        loadingCuttingData={loadingPreview}
        onClose={() => setPreviewOpen(false)}
      />

      {/* ── New DM Dialog ── */}
      <Dialog open={dmDialogOpen} onClose={() => { setDmDialogOpen(false); setDmSearchUser(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>New Direct Message</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Autocomplete
            options={allUsers}
            getOptionLabel={(u) => `${u.name} (${u.email})`}
            value={dmSearchUser}
            onChange={(_, v) => setDmSearchUser(v)}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'secondary.main' }}>{option.name[0]}</Avatar>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label="Search user" autoFocus size="small" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDmDialogOpen(false); setDmSearchUser(null); }}>Cancel</Button>
          <Button variant="contained" disabled={!dmSearchUser || creatingDm} onClick={handleCreateDm}>
            {creatingDm ? <CircularProgress size={18} /> : 'Start Chat'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── New Group Dialog ── */}
      <Dialog open={groupDialogOpen} onClose={() => { setGroupDialogOpen(false); setGroupName(''); setGroupMembers([]); }} maxWidth="xs" fullWidth>
        <DialogTitle>New Group</DialogTitle>
        <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            size="small"
            fullWidth
            autoFocus
          />
          <Autocomplete
            multiple
            options={allUsers}
            getOptionLabel={(u) => u.name}
            value={groupMembers}
            onChange={(_, v) => setGroupMembers(v)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option.name} size="small" {...getTagProps({ index })} key={option.id} />
              ))
            }
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.main' }}>{option.name[0]}</Avatar>
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label="Add members" size="small" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setGroupDialogOpen(false); setGroupName(''); setGroupMembers([]); }}>Cancel</Button>
          <Button variant="contained" disabled={!groupName.trim() || groupMembers.length === 0 || creatingGroup} onClick={handleCreateGroup}>
            {creatingGroup ? <CircularProgress size={18} /> : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
